import { Cyl, Tor } from './helpers';
import type { MatsProps, TruckMaterials } from './materials';

type Vec3 = [number, number, number];
const HALF_PI = Math.PI / 2;

function Wheel({ pos, mats }: { pos: Vec3; mats: TruckMaterials }) {
  return (
    <group position={pos}>
      <mesh material={mats.rubber}>
        <torusGeometry args={[0.23, 0.088, 18, 36]} />
      </mesh>
      <mesh material={mats.rim} rotation={[HALF_PI, 0, 0]}>
        <cylinderGeometry args={[0.246, 0.246, 0.15, 40]} />
      </mesh>
      {[1, -1].map((side) => (
        <Tor key={side} args={[0.155, 0.028, 12, 48]} mat={mats.rim} pos={[0, 0, side * 0.062]} />
      ))}
      <mesh material={mats.chrome} rotation={[HALF_PI, 0, 0]}>
        <cylinderGeometry args={[0.11, 0.11, 0.17, 28]} />
      </mesh>
      <mesh material={mats.chromeHi} rotation={[HALF_PI, 0, 0]}>
        <cylinderGeometry args={[0.04, 0.04, 0.19, 16]} />
      </mesh>
      {Array.from({ length: 5 }, (_, i) => {
        const a = (i / 5) * Math.PI * 2;
        return (
          <mesh key={i} material={mats.black} rotation={[HALF_PI, 0, 0]} position={[Math.cos(a) * 0.07, Math.sin(a) * 0.07, 0]}>
            <cylinderGeometry args={[0.012, 0.012, 0.19, 8]} />
          </mesh>
        );
      })}
    </group>
  );
}

/** 3 wheels (1 front, 2 rear) + torus fenders (accent-coloured) + front running gear. */
export function Wheels({ mats }: MatsProps) {
  return (
    <group>
      <Wheel pos={[1.3, 0.315, 0]} mats={mats} />
      <Wheel pos={[-1.02, 0.315, 0.64]} mats={mats} />
      <Wheel pos={[-1.02, 0.315, -0.64]} mats={mats} />

      {/* Rear fenders — solid torus arches over the outer wheel faces */}
      <Tor args={[0.375, 0.045, 12, 40, Math.PI]} mat={mats.accent} pos={[-1.02, 0.315, 0.712]} />
      <Tor args={[0.375, 0.045, 12, 40, Math.PI]} mat={mats.accent} pos={[-1.02, 0.315, -0.712]} />

      {/* Front fork legs */}
      {[0.1, -0.1].map((z) => (
        <Cyl key={z} args={[0.02, 0.02, 0.44, 8]} mat={mats.black} pos={[1.3, 0.56, z]} rot={[0.1, 0, 0]} />
      ))}
      {/* Front fender — shorter top cap so it reads as a fender head-on */}
      <Tor args={[0.378, 0.055, 12, 44, 0.74 * Math.PI]} mat={mats.accent} pos={[1.3, 0.315, 0]} rot={[0, 0, 0.13 * Math.PI]} />

      {/* Front grille rings */}
      {Array.from({ length: 6 }, (_, i) => (
        <Tor key={i} args={[0.05, 0.011, 8, 16]} mat={mats.chrome} pos={[1.3, 0.5 + i * 0.04, 0]} rot={[HALF_PI, 0, 0]} />
      ))}
    </group>
  );
}
